import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from 'recharts';
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  Calendar,
  RefreshCw,
  Users,
  Building2,
  CreditCard,
  Target,
  Activity,
  ArrowLeft,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAdminAnalytics } from '../../hooks/useAdminAnalytics';
import CustomSelect from '../../components/shared/CustomSelect';

const COLORS = ['#2563EB', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const AdminAdvancedAnalytics = () => {
  const {
    analytics,
    isLoading,
    error,
    period,
    setPeriod,
    periodOptions,
    refetch,
  } = useAdminAnalytics('month');

  const formatCurrency = (val) => {
    if (val == null || isNaN(val)) return '$0';
    return `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  // Chart data
  const revenueTrendData = (analytics?.trends?.revenue || []).map((t) => ({
    name: t.period,
    revenue: Number(t.revenue || 0),
  }));

  const bookingsTrendData = (analytics?.trends?.bookings || []).map((t) => ({
    name: t.period,
    bookings: Number(t.count || 0),
  }));

  const combinedTrendData = revenueTrendData.length > 0
    ? revenueTrendData.map((r, i) => ({
        name: r.name,
        revenue: r.revenue,
        bookings: (bookingsTrendData[i]?.bookings ?? 0),
      }))
    : bookingsTrendData.length > 0
      ? bookingsTrendData.map((b) => ({
          name: b.name,
          revenue: 0,
          bookings: b.bookings,
        }))
      : [];

  const bookingsByStatusData = analytics?.bookings_analytics?.by_status
    ? Object.entries(analytics.bookings_analytics.by_status).map(([status, count], i) => ({
        name: status.replace(/_/g, ' '),
        value: count,
        color: COLORS[i % COLORS.length],
      }))
    : [];

  const revenueBreakdownData = [
    { name: 'Platform Fees', value: Number(analytics?.revenue_analytics?.platform_fees || 0), color: '#2563EB' },
    { name: 'Vendor Payouts', value: Number(analytics?.revenue_analytics?.vendor_payouts || 0), color: '#10B981' },
    { name: 'Processor Fees', value: Number(analytics?.revenue_analytics?.processor_fees || 0), color: '#F59E0B' },
  ].filter((d) => d.value > 0);

  const paymentStatusData = analytics?.payment_analytics
    ? [
        { name: 'Completed', value: analytics.payment_analytics.completed || 0, color: '#10B981' },
        { name: 'Pending', value: analytics.payment_analytics.pending || 0, color: '#F59E0B' },
        { name: 'Failed', value: analytics.payment_analytics.failed || 0, color: '#EF4444' },
        { name: 'Refunded', value: analytics.payment_analytics.refunded || 0, color: '#8B5CF6' },
      ].filter((d) => d.value > 0)
    : [];

  const topVendorsData = (analytics?.top_performers?.top_vendors || []).slice(0, 8).map((v) => ({
    name: (v.business_name || 'Vendor').slice(0, 15),
    revenue: Number(v.revenue || 0),
    bookings: v.booking_count || 0,
  }));

  const topServicesData = (analytics?.top_performers?.top_services || []).slice(0, 8).map((s) => ({
    name: (s.service_name || 'Service').slice(0, 15),
    revenue: Number(s.revenue || 0),
    bookings: s.booking_count || 0,
  }));

  const userTypeData = analytics?.user_analytics?.by_type
    ? Object.entries(analytics.user_analytics.by_type).map(([type, count], i) => ({
        name: type,
        value: count,
        color: COLORS[i % COLORS.length],
      }))
    : [];

  const platformMetricsData = [
    { metric: 'Conversion', value: Number(analytics?.bookings_analytics?.conversion_rate || 0), fullMark: 100 },
    { metric: 'Completion', value: 100 - Number(analytics?.bookings_analytics?.cancellation_rate || 0), fullMark: 100 },
    { metric: 'Payment Success', value: Number(analytics?.payment_analytics?.success_rate || 0), fullMark: 100 },
    { metric: 'Vendor Verified', value: analytics?.vendor_analytics?.total > 0
      ? (analytics.vendor_analytics.verified / analytics.vendor_analytics.total) * 100 : 0, fullMark: 100 },
  ];

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6">
          <p className="text-red-600">Failed to load analytics. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white border rounded-xl shadow-sm p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Link to="/admin/analytics" className="text-sm text-gray-600 hover:text-gray-900">
                  ← Back to Analytics
                </Link>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
                <BarChart3 className="w-8 h-8" />
                Advanced Analytics
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Comprehensive platform analytics with charts and insights
              </p>
            </div>
            <div className="flex items-center gap-2">
              <CustomSelect
                value={period}
                onChange={setPeriod}
                options={periodOptions}
                className="w-40"
              />
              <button
                onClick={() => refetch()}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <RefreshCw className="w-12 h-12 animate-spin text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Loading advanced analytics...</p>
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
              <div className="bg-white rounded-xl border p-4 shadow-sm">
                <DollarSign className="w-6 h-6 text-green-600 mb-2" />
                <p className="text-xs text-gray-500 uppercase">Total Revenue</p>
                <p className="text-lg font-bold text-gray-900">{formatCurrency(analytics?.overview?.total_revenue)}</p>
              </div>
              <div className="bg-white rounded-xl border p-4 shadow-sm">
                <Calendar className="w-6 h-6 text-blue-600 mb-2" />
                <p className="text-xs text-gray-500 uppercase">Bookings</p>
                <p className="text-lg font-bold text-gray-900">{analytics?.overview?.total_bookings || 0}</p>
              </div>
              <div className="bg-white rounded-xl border p-4 shadow-sm">
                <TrendingUp className="w-6 h-6 text-purple-600 mb-2" />
                <p className="text-xs text-gray-500 uppercase">Platform Fees</p>
                <p className="text-lg font-bold text-gray-900">{formatCurrency(analytics?.overview?.platform_fees)}</p>
              </div>
              <div className="bg-white rounded-xl border p-4 shadow-sm">
                <Users className="w-6 h-6 text-indigo-600 mb-2" />
                <p className="text-xs text-gray-500 uppercase">Users</p>
                <p className="text-lg font-bold text-gray-900">{analytics?.user_analytics?.total || 0}</p>
              </div>
              <div className="bg-white rounded-xl border p-4 shadow-sm">
                <Building2 className="w-6 h-6 text-amber-600 mb-2" />
                <p className="text-xs text-gray-500 uppercase">Vendors</p>
                <p className="text-lg font-bold text-gray-900">{analytics?.vendor_analytics?.total || 0}</p>
              </div>
              <div className="bg-white rounded-xl border p-4 shadow-sm">
                <CreditCard className="w-6 h-6 text-teal-600 mb-2" />
                <p className="text-xs text-gray-500 uppercase">Payments</p>
                <p className="text-lg font-bold text-gray-900">{analytics?.payment_analytics?.total_payments || 0}</p>
              </div>
            </div>

            {/* Revenue & Bookings Trend */}
            {combinedTrendData.length > 0 && (
              <div className="bg-white rounded-xl border p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-gray-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Revenue & Bookings Over Time</h2>
                </div>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={combinedTrendData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="adminColorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#2563EB" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="left" tickFormatter={(v) => `$${v}`} />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip
                        formatter={(value, name) => [
                          name === 'revenue' ? formatCurrency(value) : value,
                          name === 'revenue' ? 'Revenue' : 'Bookings',
                        ]}
                      />
                      <Legend />
                      <Area yAxisId="left" type="monotone" dataKey="revenue" stroke="#2563EB" fill="url(#adminColorRevenue)" name="Revenue" strokeWidth={2} />
                      <Line yAxisId="right" type="monotone" dataKey="bookings" stroke="#10B981" strokeWidth={2} dot={{ r: 4 }} name="Bookings" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Two-column: Bookings by Status & Revenue Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {bookingsByStatusData.length > 0 && (
                <div className="bg-white rounded-xl border p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <Calendar className="w-5 h-5 text-gray-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Bookings by Status</h2>
                  </div>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={bookingsByStatusData}
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          dataKey="value"
                          nameKey="name"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {bookingsByStatusData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {revenueBreakdownData.length > 0 && (
                <div className="bg-white rounded-xl border p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <DollarSign className="w-5 h-5 text-gray-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Revenue Breakdown</h2>
                  </div>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={revenueBreakdownData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          dataKey="value"
                          nameKey="name"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {revenueBreakdownData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            {/* Platform Metrics Radar & Payment Status */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <Target className="w-5 h-5 text-gray-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Platform Metrics</h2>
                </div>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={platformMetricsData}>
                      <PolarGrid stroke="#E5E7EB" />
                      <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
                      <PolarRadiusAxis angle={90} domain={[0, 100]} />
                      <Radar name="Score %" dataKey="value" stroke="#2563EB" fill="#2563EB" fillOpacity={0.4} strokeWidth={2} />
                      <Tooltip formatter={(v) => [`${Number(v).toFixed(1)}%`, 'Score']} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {paymentStatusData.length > 0 && (
                <div className="bg-white rounded-xl border p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <CreditCard className="w-5 h-5 text-gray-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Payment Status</h2>
                  </div>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={paymentStatusData}
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          dataKey="value"
                          nameKey="name"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {paymentStatusData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            {/* Top Vendors & Top Services */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {topVendorsData.length > 0 && (
                <div className="bg-white rounded-xl border p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <Building2 className="w-5 h-5 text-gray-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Top Vendors by Revenue</h2>
                  </div>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={topVendorsData} layout="vertical" margin={{ top: 5, right: 30, left: 70, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis type="number" tickFormatter={(v) => `$${v}`} />
                        <YAxis type="category" dataKey="name" width={65} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => [formatCurrency(v), 'Revenue']} />
                        <Bar dataKey="revenue" fill="#2563EB" radius={[0, 4, 4, 0]} name="Revenue" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {topServicesData.length > 0 && (
                <div className="bg-white rounded-xl border p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <Activity className="w-5 h-5 text-gray-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Top Services by Revenue</h2>
                  </div>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={topServicesData} layout="vertical" margin={{ top: 5, right: 30, left: 70, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis type="number" tickFormatter={(v) => `$${v}`} />
                        <YAxis type="category" dataKey="name" width={65} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => [formatCurrency(v), 'Revenue']} />
                        <Bar dataKey="revenue" fill="#10B981" radius={[0, 4, 4, 0]} name="Revenue" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            {/* User Types */}
            {userTypeData.length > 0 && (
              <div className="bg-white rounded-xl border p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-gray-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Users by Type</h2>
                </div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={userTypeData}
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {userTypeData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Detailed Metrics */}
            <div className="bg-white rounded-xl border p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Detailed Metrics</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Avg Booking Value</p>
                  <p className="text-xl font-bold text-gray-900">{formatCurrency(analytics?.bookings_analytics?.average_value)}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Conversion Rate</p>
                  <p className="text-xl font-bold text-gray-900">{(analytics?.bookings_analytics?.conversion_rate || 0).toFixed(1)}%</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Cancellation Rate</p>
                  <p className="text-xl font-bold text-gray-900">{(analytics?.bookings_analytics?.cancellation_rate || 0).toFixed(1)}%</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Payment Success Rate</p>
                  <p className="text-xl font-bold text-gray-900">{(analytics?.payment_analytics?.success_rate || 0).toFixed(1)}%</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminAdvancedAnalytics;
